import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axios from 'axios';

const AVAILABLE_FIELDS = [
    { key: 'first_name', label: 'First Name', category: 'Basic Info' },
    { key: 'last_name', label: 'Last Name', category: 'Basic Info' },
    { key: 'email', label: 'Email', category: 'Contact Info' },
    { key: 'phone_number', label: 'Phone Number', category: 'Contact Info' },
    { key: 'gender', label: 'Gender', category: 'Personal Info' },
    { key: 'birthdate', label: 'Birth Date', category: 'Personal Info' },
    { key: 'shirt_size', label: 'T-Shirt Size', category: 'Registration Info' },
    { key: 'team_name', label: 'Team Name', category: 'Team Info' },
    { key: 'league_name', label: 'League Name', category: 'League Info' },
    { key: 'is_active', label: 'Active Status', category: 'Status' },
    { key: 'paid', label: 'Payment Status', category: 'Status' },
    { key: 'waiver_signed', label: 'Waiver Signed', category: 'Waiver Info' },
    { key: 'waiver_signed_date', label: 'Waiver Signed Date', category: 'Waiver Info' },
    { key: 'waiver_version', label: 'Waiver Version', category: 'Waiver Info' },
    { key: 'waiver_title', label: 'Waiver Title', category: 'Waiver Info' },
];

function CSVExportModal({ show, handleClose, activeSeasons, selectedSeason }) {
    const [selectedFields, setSelectedFields] = useState([
        'first_name', 'last_name', 'email', 'phone_number',
        'gender', 'shirt_size', 'team_name', 'waiver_signed'
    ]);
    const [exportSeason, setExportSeason] = useState(selectedSeason || 'all');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleFieldToggle = (fieldKey) => {
        setSelectedFields(prev =>
            prev.includes(fieldKey)
                ? prev.filter(f => f !== fieldKey)
                : [...prev, fieldKey]
        );
    };

    const handleSelectAll = () => {
        setSelectedFields(AVAILABLE_FIELDS.map(f => f.key));
    };

    const handleSelectNone = () => {
        setSelectedFields([]);
    };

    const handleExport = async () => {
        if (selectedFields.length === 0) {
            setError('Please select at least one field to export');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                fields: selectedFields.join(','),
                season_id: exportSeason
            });

            const response = await axios.get(`/api/player/export-csv/?${params}`, {
                responseType: 'blob',
                withCredentials: true,
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `players_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            handleClose();
        } catch (error) {
            console.error('Export error:', error);
            setError('Failed to export CSV. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const groupedFields = AVAILABLE_FIELDS.reduce((acc, field) => {
        if (!acc[field.category]) {
            acc[field.category] = [];
        }
        acc[field.category].push(field);
        return acc;
    }, {});

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Export Players to CSV</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        {error}
                    </Alert>
                )}

                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Season Filter</Form.Label>
                        <Form.Select
                            value={exportSeason}
                            onChange={(e) => setExportSeason(e.target.value)}
                        >
                            <option value="all">All Seasons</option>
                            {activeSeasons.map((season) => (
                                <option key={season.id} value={season.id}>
                                    {season.name} ({season.league__name})
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Choose which season's players to export. Select "All Seasons" to export all players.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Select Fields to Export</Form.Label>
                        <div className="mb-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleSelectAll}
                                className="me-2"
                            >
                                Select All
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleSelectNone}
                            >
                                Select None
                            </Button>
                        </div>

                        {Object.entries(groupedFields).map(([category, fields]) => (
                            <div key={category} className="mb-3">
                                <h6 className="text-muted">{category}</h6>
                                <div className="row">
                                    {fields.map((field) => (
                                        <div key={field.key} className="col-md-6">
                                            <Form.Check
                                                type="checkbox"
                                                id={`field-${field.key}`}
                                                label={field.label}
                                                checked={selectedFields.includes(field.key)}
                                                onChange={() => handleFieldToggle(field.key)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleExport}
                    disabled={loading || selectedFields.length === 0}
                >
                    {loading ? 'Exporting...' : 'Export CSV'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default CSVExportModal; 